import { useCallback, useRef, useState } from 'react';

type Direction = 'horizontal' | 'vertical';

type Scope = 'self' | 'subtree';

export function useIsOverflowing(
  direction: Direction = 'horizontal',
  scope: Scope = 'self',
): [React.RefCallback<HTMLElement>, boolean] {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      mutationObserverRef.current?.disconnect();
      mutationObserverRef.current = null;

      if (node) {
        const isElementOverflowing = (element: Element) =>
          direction === 'horizontal'
            ? element.scrollWidth > element.clientWidth
            : element.scrollHeight > element.clientHeight;
        const check = () => {
          const elements = scope === 'subtree' ? [node, ...node.querySelectorAll('*')] : [node];
          setIsOverflowing(elements.some(isElementOverflowing));
        };
        check();
        mutationObserverRef.current = new MutationObserver(check);
        mutationObserverRef.current.observe(node, {
          childList: true,
          characterData: true,
          subtree: true,
        });
        if (typeof ResizeObserver === 'undefined') return;
        observerRef.current = new ResizeObserver(check);
        observerRef.current.observe(node);
      }
    },
    [direction, scope],
  );

  return [ref, isOverflowing];
}
